/**
 * useNFCBridge Hook
 * 
 * React hook for NFC Bridge integration
 */

import { useState, useEffect, useCallback } from 'react';
import { nfcBridgeService } from '@/services/nfcBridgeService';

export interface NFCBridgeStatus {
  available: boolean;
  enabled: boolean;
  platform: 'android' | 'ios' | 'unknown';
}

export function useNFCBridge() {
  const [status, setStatus] = useState<NFCBridgeStatus>({
    available: false,
    enabled: false,
    platform: 'unknown',
  });
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      setIsChecking(true);
      try {
        const bridgeStatus = await nfcBridgeService.getStatus();
        setStatus(bridgeStatus);
      } catch (error) {
        console.error('[useNFCBridge] Error checking status:', error);
        setStatus({
          available: false,
          enabled: false,
          platform: 'unknown',
        });
      } finally {
        setIsChecking(false);
      }
    };

    checkStatus();

    // Check periodically
    const interval = setInterval(checkStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const readCard = useCallback(async (options: {
    amount: number;
    currency: string;
  }) => {
    return await nfcBridgeService.readCard(options);
  }, []);

  const startTapToPay = useCallback(
    async (options: { tapSessionId: string; accessToken: string }) => {
      return await nfcBridgeService.startTapToPay(options);
    },
    [],
  );

  return {
    status,
    isChecking,
    readCard,
    startTapToPay,
    isAvailable: status.available && status.enabled,
    isMobile: nfcBridgeService.isMobileDevice(),
    downloadLinks: nfcBridgeService.getDownloadLink(),
  };
}

